import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, InputGroup, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { TextareaField } from '@/components/TextareaField';
import { AudioPreview } from '@/components/AudioPreview';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useDeleteAnthemFileMutation, useGetAnthem, usePostAnthemMutation, usePutAnthemMutation } from '@/services/queries/anthem';
import { GetParamsObject } from '@/model/query-params';
import { singleTeamAnthemSchema, SingleTeamAnthemFormData } from './schemas';

export const AnthemSingleTeamForm = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const params = GetParamsObject({}, 1, 1, 'desc', 'created_at');
  const { data: anthemData, isLoading, forceRefetch } = useGetAnthem(params);
  const existingAnthem = anthemData?.data?.[0];

  const postMutation = usePostAnthemMutation();
  const putMutation = usePutAnthemMutation();
  const deleteFileMutation = useDeleteAnthemFileMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    reset,
  } = useForm<SingleTeamAnthemFormData>({
    resolver: zodResolver(singleTeamAnthemSchema),
    defaultValues: { lyrics: '', file: undefined },
  });

  useEffect(() => {
    if (!existingAnthem) return;
    reset({ lyrics: existingAnthem.lyrics ?? '', file: undefined });
  }, [existingAnthem, reset]);

  const onSubmit = useCallback(
    (data: SingleTeamAnthemFormData) => {
      if (existingAnthem) {
        const payload: any = { lyrics: data.lyrics ?? '' };
        if (data.file) payload.file = data.file;

        putMutation.mutate(
          { id: existingAnthem.uuid, payload },
          {
            onSuccess: () => {
              toast({ status: 'success', title: t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_success') });
              forceRefetch();
            },
            onError: (resp: any) => {
              toast({ status: 'error', title: (resp && resp.message) || t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_error') });
            },
          },
        );
      } else {
        if (!data.file) {
          toast({ status: 'error', title: t('pages.application_history_anthem.schema.application_history_anthem_required_path') });
          return;
        }

        postMutation.mutate(
          { lyrics: data.lyrics ?? '', file: data.file },
          {
            onSuccess: () => {
              toast({ status: 'success', title: t('pages.application_history_anthem.anthem_create_modal.anthem_create_success') });
              forceRefetch();
            },
            onError: (resp: any) => {
              toast({ status: 'error', title: (resp && resp.message) || t('pages.application_history_anthem.anthem_create_modal.anthem_create_error') });
            },
          },
        );
      }
    },
    [existingAnthem, putMutation, postMutation, toast, t, forceRefetch],
  );

  const handleDeleteFile = useCallback(() => {
    if (!existingAnthem) return;
    deleteFileMutation.mutate(existingAnthem.uuid, {
      onSuccess: () => {
        toast({ status: 'success', title: t('pages.application_history_anthem.application_history_anthem_delete_success') });
        forceRefetch();
      },
      onError: (resp: any) => {
        toast({ status: 'error', title: (resp && resp.message) || t('pages.application_history_anthem.application_history_anthem_delete_error') });
      },
    });
  }, [existingAnthem, deleteFileMutation, toast, t, forceRefetch]);

  return (
    <>
      <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6} px={4} mt={4}>
        <Text fontWeight='medium'>{t('pages.application_history_anthem.anthem_create_modal.anthem_label_lyrics')}</Text>
        <TextareaField
          rows={6}
          maxLength={2000}
          helperText={t('pages.application_history_anthem.anthem_create_modal.anthem_helper_text')}
          error={errors?.lyrics?.message}
          {...register('lyrics')}
        />

        <InputGroup display='flex' flexDirection='column' width='100%'>
          <AudioPreview
            fileUrl={existingAnthem?.location ?? ''}
            onFileChange={file => setValue('file', file)}
            onDelete={handleDeleteFile}
            error={errors?.file?.message?.toString()}
          />
        </InputGroup>
      </Flex>

      <Flex justifyContent={{ base: 'flex-end', md: 'flex-start' }} gap={6} mt={4}>
        <Button variant='link' color='brand.500' textDecoration='underline' onClick={() => navigate(-1)}>
          {t('pages.application_social_media.social_media_back')}
        </Button>
        <Button onClick={() => formRef?.current?.requestSubmit()} isLoading={postMutation.isLoading || putMutation.isLoading}>
          {t('pages.application_social_media.social_media_save')}
        </Button>
      </Flex>

      <CenteredSpinner isLoading={isLoading || deleteFileMutation.isLoading} />
    </>
  );
};
